import type { IFilter } from "../api/models";
import { defaultAdvancedFilter, parseSavedFilter, type AdvancedFilter } from "../filters/filter-logic";
import { QUICK_FILTERS, type QuickFilterCode } from "./quick-filters";

// WHAT THE IN PLAY LIST IS FILTERED BY. `null` ON THE PAGE MEANS "NOT CHOSEN YET": THE DEFAULT
// QUICK FILTER, WHICH FOLLOWS THE ROLES ONCE THEY LOAD.
export type AppliedFilter = { kind: "quick"; code: QuickFilterCode } | { kind: "advanced"; filter: AdvancedFilter } | { kind: "cleared" };

// `local_storage_filter_key` IN THE ANGULAR IN PLAY PAGE: THE LAST FILTER, KEPT ACROSS VISITS
// UNTIL "CLEAR FILTER" REMOVES IT
export const STORED_FILTER_KEY = "LAST_IN_PLAY_FILTER";

// THE ANGULAR PAGE STORES ITS WHOLE `current_filter` (AN `AdvancedFilter`). A QUICK FILTER IS
// RECOGNIZED BY ITS `role` (THE ROLE GROUP THAT GATES IT), AS ANGULAR'S `buildQuickFilter` DOES.
export function toStored(f: AppliedFilter, label: string, filters: IFilter[]): string | null {
  if (f.kind === "cleared") return null;
  if (f.kind === "quick") {
    const qf = QUICK_FILTERS.find((q) => q.code === f.code);
    return JSON.stringify({ ...defaultAdvancedFilter(), role: qf?.role ?? "", label, type: "quick", active: true, filters });
  }
  return JSON.stringify({ ...f.filter, label, type: "advanced", active: true, filters });
}

export function fromStored(json: string | null): AppliedFilter | null {
  if (!json) return null;
  const parsed = parseSavedFilter(json);
  if (!parsed) return null;
  const quick = QUICK_FILTERS.find((q) => q.role === parsed.role);
  if (quick) return { kind: "quick", code: quick.code };
  // AN ADVANCED FILTER IS RESTORED WITH THE CLAUSES IT WAS APPLIED WITH; ONE WITHOUT ANY IS NOT
  // A FILTER AND FALLS BACK TO THE DEFAULT
  if (parsed.filters.length === 0) return null;
  return { kind: "advanced", filter: parsed };
}

// STORAGE CAN BE MISSING OR BLOCKED; NEITHER MAY BREAK THE PAGE
export function readStoredFilter(): AppliedFilter | null {
  try {
    return fromStored(localStorage.getItem(STORED_FILTER_KEY));
  } catch {
    return null;
  }
}

export function writeStoredFilter(value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(STORED_FILTER_KEY);
    else localStorage.setItem(STORED_FILTER_KEY, value);
  } catch {
    // NOTHING TO PERSIST INTO
  }
}
